import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import SiteHome from "@/pages/site/SiteHome";
import SiteServices from "@/pages/site/SiteServices";
import SiteAbout from "@/pages/site/SiteAbout";
import SiteContact from "@/pages/site/SiteContact";
import SiteCalculator from "@/pages/site/SiteCalculator";
import SiteServiceDetail from "@/pages/site/SiteServiceDetail";
import SiteBlog from "@/pages/site/SiteBlog";
import SiteBlogPost from "@/pages/site/SiteBlogPost";
import SiteSoftware from "@/pages/site/SiteSoftware";
import SiteTrusted from "@/pages/site/SiteTrusted";
import ReportShare from "@/pages/ReportShare";
import AdminSendInfo from "@/pages/AdminSendInfo";
import AdminProposals from "@/pages/AdminProposals";
import AdminCalls from "@/pages/AdminCalls";
import PublicProposal from "@/pages/PublicProposal";
import PublicCall from "@/pages/PublicCall";
import SiteGallery from "@/pages/site/SiteGallery";
import StaffLogin from "@/pages/StaffLogin";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import PatientDetail from "@/pages/PatientDetail";
import PatientReport from "@/pages/PatientReport";
import OrganizerReport from "@/pages/OrganizerReport";
import LiveEvent from "@/pages/LiveEvent";
import Calendar from "@/pages/Calendar";
import LostChildren from "@/pages/LostChildren";
import SelfCheckin from "@/pages/SelfCheckin";
import Rota from "@/pages/Rota";
import Wall from "@/pages/Wall";
import ControlLogin from "@/pages/ControlLogin";
import ControlOverview from "@/pages/ControlOverview";
import ControlEvent from "@/pages/ControlEvent";
import AdminHub from "@/pages/AdminHub";
import ClientContacts from "@/pages/ClientContacts";
import Invoices from "@/pages/Invoices";
import ClientPortal from "@/pages/ClientPortal";
import FightCard from "@/pages/FightCard";
import MedicalPlan from "@/pages/MedicalPlan";
import AdminWebsite from "@/pages/AdminWebsite";
import AdminJobs from "@/pages/AdminJobs";
import AdminBroadcast from "@/pages/AdminBroadcast";
import AdminMarketing from "@/pages/AdminMarketing";
import AdminEmails from "@/pages/AdminEmails";
import SignatureBuilder from "@/pages/SignatureBuilder";
import PortalLogin from "@/pages/PortalLogin";
import PortalHome from "@/pages/PortalHome";
import PortalSetPassword from "@/pages/PortalSetPassword";
import MyShifts from "@/pages/MyShifts";
import AdminStaff from "@/pages/AdminStaff";
import StaffRegister from "@/pages/StaffRegister";
import PublicIntake from "@/pages/PublicIntake";
import QuestionsForm from "@/pages/QuestionsForm";
import AdminLinks from "@/pages/AdminLinks";
import PersonalCalendar from "@/pages/PersonalCalendar";
import FighterForm, { FighterFormEvent } from "@/pages/FighterForm";
import ChatWidget from "@/components/ChatWidget";

const PortalAwareChat = () => {
  const { pathname } = useLocation();
  if (pathname.startsWith("/portal")) return null;
  return <ChatWidget />;
};
import { Eye } from "lucide-react";
import { api } from "@/lib/api";

function AdminOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return <div className="min-h-screen grid place-items-center bg-[#F4F7FA] text-slate-400 text-sm">Loading…</div>;
  }
  if (!user) return <Navigate to="/staff/login" replace />;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen grid place-items-center bg-[#F4F7FA]">
        <div className="flex flex-col items-center gap-3">
          <div className="h-10 w-10 rounded-full border-4 border-[#005EB8] border-t-transparent animate-spin" />
          <p className="text-sm text-slate-500 font-medium">Loading portal…</p>
        </div>
      </div>
    );
  if (!user) return <Navigate to="/staff/login" replace />;
  return children;
}

function ImpersonationBanner() {
  const { user } = useAuth();
  if (!user?.impersonating) return null;
  return (
    <div data-testid="impersonation-banner"
      className="bg-amber-400 text-slate-950 text-center text-xs sm:text-sm font-bold py-2.5 px-4 flex items-center justify-center gap-3 sticky top-0 z-[60] flex-wrap">
      <Eye className="h-4 w-4 shrink-0" />
      <span>Viewing as {user.name} ({user.role_label}) — this is exactly what they see</span>
      <button onClick={async () => { await api.post("/staff/stop-impersonating").catch(() => {}); window.location.href = "/dashboard"; }}
        data-testid="stop-impersonating-btn"
        className="underline underline-offset-2 font-black hover:text-slate-700 transition-colors">
        Back to my admin view
      </button>
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ImpersonationBanner />
          <Routes>
            <Route path="/" element={<SiteHome />} />
            <Route path="/services" element={<SiteServices />} />
            <Route path="/services/:slug" element={<SiteServiceDetail />} />
            <Route path="/about-us" element={<SiteAbout />} />
            <Route path="/contact-us" element={<SiteContact />} />
            <Route path="/staff-calculator" element={<SiteCalculator />} />
            <Route path="/blog" element={<SiteBlog />} />
            <Route path="/blog/:slug" element={<SiteBlogPost />} />
            <Route path="/software" element={<SiteSoftware />} />
            <Route path="/people-we-trust" element={<SiteTrusted />} />
            <Route path="/report/:token" element={<ReportShare />} />
            <Route path="/gallery" element={<SiteGallery />} />
            <Route path="/hub" element={<Landing />} />
            <Route path="/live/:token" element={<LiveEvent />} />
            <Route path="/wall/:token" element={<Wall />} />
            <Route path="/checkin/:token" element={<SelfCheckin />} />
            <Route path="/fight-form/:token" element={<FighterForm />} />
            <Route path="/fight-form/event/:token" element={<FighterFormEvent />} />
            <Route path="/staff/login" element={<StaffLogin />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/control-login" element={<ControlLogin />} />
            <Route path="/control" element={<ControlOverview />} />
            <Route path="/control/event/:id" element={<ControlEvent />} />
            <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
            <Route path="/calendar" element={<Protected><Calendar /></Protected>} />
            <Route path="/rota" element={<Protected><AdminOnly><Rota /></AdminOnly></Protected>} />
            <Route path="/my" element={<Protected><MyShifts /></Protected>} />
            <Route path="/admin/staff" element={<Protected><AdminOnly><AdminStaff /></AdminOnly></Protected>} />
            <Route path="/lost-children" element={<Protected><LostChildren /></Protected>} />
            <Route path="/patients/:id" element={<Protected><PatientDetail /></Protected>} />
            <Route path="/patients/:id/report" element={<Protected><PatientReport /></Protected>} />
            <Route path="/reports/organizer" element={<Protected><OrganizerReport /></Protected>} />
            <Route path="/admin" element={<Protected><AdminOnly><AdminHub /></AdminOnly></Protected>} />
            <Route path="/admin/contacts" element={<Protected><AdminOnly><ClientContacts /></AdminOnly></Protected>} />
            <Route path="/admin/invoices" element={<Protected><AdminOnly><Invoices /></AdminOnly></Protected>} />
            <Route path="/admin/client-portal" element={<Protected><AdminOnly><ClientPortal /></AdminOnly></Protected>} />
            <Route path="/events/:eid/fight-card" element={<Protected><FightCard /></Protected>} />
            <Route path="/admin/medical-plan" element={<Protected><AdminOnly><MedicalPlan /></AdminOnly></Protected>} />
            <Route path="/admin/website" element={<Protected><AdminOnly><AdminWebsite /></AdminOnly></Protected>} />
            <Route path="/admin/send-info" element={<Protected><AdminOnly><AdminSendInfo /></AdminOnly></Protected>} />
            <Route path="/admin/proposals" element={<Protected><AdminOnly><AdminProposals /></AdminOnly></Protected>} />
            <Route path="/proposal/:token" element={<PublicProposal />} />
            <Route path="/admin/calls" element={<Protected><AdminOnly><AdminCalls /></AdminOnly></Protected>} />
            <Route path="/call/:token" element={<PublicCall />} />
            <Route path="/admin/jobs" element={<Protected><AdminOnly><AdminJobs /></AdminOnly></Protected>} />
            <Route path="/admin/broadcast" element={<Protected><AdminOnly><AdminBroadcast /></AdminOnly></Protected>} />
            <Route path="/admin/marketing" element={<Protected><AdminOnly><AdminMarketing /></AdminOnly></Protected>} />
            <Route path="/admin/emails" element={<Protected><AdminOnly><AdminEmails /></AdminOnly></Protected>} />
            <Route path="/admin/emails/signature" element={<Protected><AdminOnly><SignatureBuilder /></AdminOnly></Protected>} />
            <Route path="/admin/links" element={<Protected><AdminOnly><AdminLinks /></AdminOnly></Protected>} />
            <Route path="/admin/my-calendar" element={<Protected><AdminOnly><PersonalCalendar /></AdminOnly></Protected>} />
            <Route path="/portal/login" element={<PortalLogin />} />
            <Route path="/join" element={<StaffRegister />} />
            <Route path="/portal" element={<PortalHome />} />
            <Route path="/portal/set-password/:token" element={<PortalSetPassword />} />
            <Route path="/intake" element={<PublicIntake />} />
            <Route path="/questions/:token" element={<QuestionsForm />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <PortalAwareChat />
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
